import { useContext } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import { convertUTCToTransactionsContextTimeZone } from 'components/utils';
import { useMediaQuery } from 'services/useMediaQUery';
import { amountFormattedValue, isCryptoProvider, MEDIA_QUERY } from 'services/utils';
import { ActionButton } from 'uikit/ActionButton/ActionButton';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import './UnapprovedPaymentsTable.scss';

const UnapprovedPaymentsTable = ({
  transactions,
  selectedTransactions,
  onRemove,
  onEdit,
  onSelect,
  onSelectAll,
  userAccounts
}) => {
  const i18n = useContext(i18nContext);
  const isMobile = useMediaQuery(MEDIA_QUERY.MOBILE);

  const areAllSelected = transactions.length > 0 && selectedTransactions.length === transactions.length;
  const areSomeSelected = selectedTransactions.length > 0 && selectedTransactions.length < transactions.length;

  if (isMobile) {
    return (
      <div className={'unapproved-payments-table'}>
        {transactions.map((transaction, index) => {
          const updatedAt = convertUTCToTransactionsContextTimeZone(transaction.updatedAt);
          const isSelected = selectedTransactions
            .map((selectedTransaction) => selectedTransaction.id)
            .includes(transaction.id);
          const senderAccount = userAccounts.find(
            (userAccount) => userAccount.wallet_number === transaction?.walletNumber
          );

          return (
            <div className={`unapproved-transaction-item-mobile ${isSelected ? 'selected' : ''}`} key={index}>
              <div className={'unapproved-transaction-item-actions-and-checkbox'}>
                <CheckBox
                  isActive={isSelected}
                  showCheckBox={true}
                  onChange={() => onSelect(transaction, !isSelected)}
                />
                <div className={'unapproved-transaction-item-actions'}>
                  <ActionButton
                    tooltipText={i18n.getMessage('unapprovedPayments.table.remove.tooltip')}
                    iconType={'remove'}
                    onClick={() => onRemove(transaction.id)}
                    isSelected={isSelected}
                  />
                  <ActionButton
                    tooltipText={
                      !senderAccount
                        ? i18n.getMessage('unapprovedPayments.table.editIsDisabled.tooltip')
                        : i18n.getMessage('unapprovedPayments.table.edit.tooltip')
                    }
                    iconType={'edit-for-approval'}
                    onClick={() => onEdit(transaction.id)}
                    isSelected={isSelected}
                    isDisabled={!senderAccount}
                  />
                </div>
              </div>
              <div className={'unapproved-transaction-item-row'}>
                <div className={'unapproved-transaction-item-head-label'}>
                  {i18n.getMessage('unapprovedPayments.table.date')}
                </div>
                <div className={'unapproved-transaction-item-cell-content'}>
                  {`${updatedAt.date} ${updatedAt.time}`}
                </div>
              </div>
              <div className={'unapproved-transaction-item-row'}>
                <div className={'unapproved-transaction-item-head-label'}>
                  {i18n.getMessage('unapprovedPayments.table.payeeName')}
                </div>
                <div className={'unapproved-transaction-item-cell-content'}>{transaction.counterpartyName}</div>
              </div>
              <div className={'unapproved-transaction-item-row'}>
                <div className={'unapproved-transaction-item-head-label'}>
                  {i18n.getMessage('unapprovedPayments.table.purposeOfPayment')}
                </div>
                <div className={'unapproved-transaction-item-cell-content'}>{transaction.comment}</div>
              </div>
              <div className={'unapproved-transaction-item-row'}>
                <div className={'unapproved-transaction-item-head-label'}>
                  {i18n.getMessage('unapprovedPayments.table.amount')}
                </div>
                <div className={'unapproved-transaction-item-cell-content'}>
                  {`${transaction.amountDetails?.displayAmount || amountFormattedValue(transaction.amount, 2)}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={'unapproved-payments-table'}>
      <div className={'table-header'}>
        <div className={'checkbox-cell'}>
          <CheckBox
            isActive={areAllSelected}
            showCheckBox={true}
            isIndeterminate={areSomeSelected}
            onChange={() => onSelectAll(!areAllSelected)}
          />
        </div>
        <div className={'date-cell'}>{i18n.getMessage('unapprovedPayments.table.date')}</div>
        <div className={'payee-cell'}>{i18n.getMessage('unapprovedPayments.table.payeeName')}</div>
        <div className={'purpose-cell'}>{i18n.getMessage('unapprovedPayments.table.purposeOfPayment')}</div>
        <div className={'amount-cell'}>{i18n.getMessage('unapprovedPayments.table.amount')}</div>
        <div className={'edit-cell'}>{i18n.getMessage('unapprovedPayments.table.actions')}</div>
      </div>
      <div className={'table-body'}>
        {transactions.map((transaction) => {
          const updatedAt = convertUTCToTransactionsContextTimeZone(transaction.updatedAt);
          const isSelected = selectedTransactions
            .map((selectedTransaction) => selectedTransaction.id)
            .includes(transaction.id);
          const senderAccount = userAccounts.find(
            (userAccount) => userAccount.wallet_number === transaction?.walletNumber
          );

          return (
            <div key={transaction.id} className={`table-row ${isSelected ? 'selected' : ''}`}>
              <div className={'checkbox-cell'}>
                <CheckBox
                  isActive={isSelected}
                  showCheckBox={true}
                  onChange={() => onSelect(transaction, !isSelected)}
                />
              </div>
              <div className={'date-cell'}>{`${updatedAt.date} ${updatedAt.time}`}</div>
              <div className={'payee-cell'}>{transaction.counterpartyName}</div>
              <div className={'purpose-cell'}>{transaction.comment}</div>
              <div className={'amount-cell'}>
                {`${transaction.amountDetails?.displayAmount || amountFormattedValue(transaction.amount, 2)}`}
              </div>
              <div className={'edit-cell'}>
                <ActionButton
                  tooltipText={i18n.getMessage('unapprovedPayments.table.remove.tooltip')}
                  iconType={'remove'}
                  onClick={() => onRemove(transaction.id)}
                  isSelected={isSelected}
                />
                <ActionButton
                  tooltipText={
                    !senderAccount
                      ? i18n.getMessage('unapprovedPayments.table.editIsDisabled.tooltip')
                      : i18n.getMessage('unapprovedPayments.table.edit.tooltip')
                  }
                  iconType={'edit-for-approval'}
                  onClick={() => onEdit(transaction.id)}
                  isSelected={isSelected}
                  isDisabled={!senderAccount}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

UnapprovedPaymentsTable.propTypes = {
  transactions: PropTypes.array.isRequired,
  selectedTransactions: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  userAccounts: PropTypes.array
};

export default UnapprovedPaymentsTable;
